import { useCallback, memo } from 'react';

import HeaderCell from './HeaderCell';
import type { CalculatedColumn, DataGridProps, FilterOptions, Filters, ISelectRange, SortInfo, ISelectorEvent } from './types';
import { headerRowClassname } from './style';

type SharedDataGridProps<R, SR> = Pick<DataGridProps<R, SR>, 'onSort'>;

export interface HeaderRowProps<R, SR> extends SharedDataGridProps<R, SR> {
  columns: readonly CalculatedColumn<R, SR>[];
  allRowsSelected: boolean;
  selectedRange: ISelectRange;
  onColumnResize: (column: CalculatedColumn<R, SR>, width: number) => void;
  lastFrozenColumnIndex: number;
  selectedColumns: Set<string>;
  onGridSelect:  (data: ISelectorEvent) => void;
  sortInfo: SortInfo;
  enableSortRow: boolean;
  rowFilterOptions: FilterOptions;
  rowFilters?: Filters;
  onFiltersChange?: (newFilters: Filters) => void;
  onColumnReorder?: (sourceColumnKey: string, targetColumnKey: string) => void;
}

function HeaderRow<R, SR>({
  columns,
  onColumnReorder,
  allRowsSelected,
  selectedRange,
  selectedColumns,
  onGridSelect,
  onColumnResize,
  sortInfo,
  enableSortRow,
  rowFilterOptions,
  rowFilters,
  onFiltersChange,
  onSort,
  lastFrozenColumnIndex
}: HeaderRowProps<R, SR>) {

  const cells = [];
  const onCellFiltersChange = useCallback((key: string, newFilters?: Set<any>) => {
    onFiltersChange?.({
      ...rowFilters,
      [key]: newFilters
    })
  }, [onFiltersChange, rowFilters])
  for (let index = 0; index < columns.length; index++) {
    const column = columns[index];
    const [minIdx, maxIdx] = [selectedRange.columnIdx, selectedRange.endColumnIdx].sort((a, b) => a - b);
    const isColumnCellSelect = maxIdx >= column.idx && column.idx >= minIdx;
    const isCellSelected = isColumnCellSelect || selectedColumns.has(column.key);
    cells.push(
      <HeaderCell<R, SR>
        key={column.key}
        column={column}
        columnIdx={column.idx}
        filters={rowFilters?.[column.key]}
        onCellFiltersChange={onCellFiltersChange}
        isCellSelected={isCellSelected}
        onResize={onColumnResize}
        allRowsSelected={allRowsSelected}
        onSort={onSort}
        sortInfo={sortInfo}
        enableSortRow={enableSortRow}
        cellFilters={rowFilterOptions?.[column.key]}
        onGridSelect={onGridSelect}
        onColumnReorder={onColumnReorder}
      />
    );
  }

  return (
    <div
      role="row"
      aria-rowindex={1} // aria-rowindex is 1 based
      className={headerRowClassname}
    >
      {cells}
    </div>
  );
}

export default memo(HeaderRow) as typeof HeaderRow;
